import * as THREE from "three";
import {
  DEFAULT_ARENA_LAYOUT,
  MINE_ARM_DELAY_MS,
  OVERDRIVE_ACCEL_MULTIPLIER,
  OVERDRIVE_SPEED_MULTIPLIER,
  teamColor,
  type VehicleBlueprint,
  type RenderMode,
  type GameMode,
} from "@neon-arena/shared";
import { PhysicsWorld } from "../physics/PhysicsWorld.js";
import { VehicleController } from "../physics/VehicleController.js";
import { ArenaRenderer } from "../rendering/ArenaRenderer.js";
import { buildVehicleVisual, triggerFlash, tickFlash, tickWheelSpin, updateDamageZones, type VehicleVisual } from "../rendering/VehicleRenderer.js";
import { DroneCameraController } from "./DroneCameraController.js";
import { EffectsSystem } from "../rendering/EffectsSystem.js";
import { SceneManager, type CameraMode } from "./SceneManager.js";
import { InputManager, type CameraInput } from "../input/InputManager.js";
import { ClientNetwork, type MineView, type PlayerView } from "../networking/ClientNetwork.js";
import { Hud } from "../ui/Hud.js";
import { announcer } from "../ui/announcer.js";
import { matchAnnouncer } from "../ui/announcerMatch.js";
import { AudioSystem } from "../audio/AudioSystem.js";
import { EngineAudio } from "../audio/EngineAudio.js";
import { computeStats, computeWeaponStats, CAR_PRESETS, DEFAULT_UPGRADES, DEFAULT_WEAPON_UPGRADES, DEFAULT_GAME_CONFIG, type CarUpgrade, type DummyPreset, type WeaponUpgrade } from "@neon-arena/shared";
import type { ControlMethod } from "../ui/VehicleSelect.js";
import { DebugOverlay } from "./DebugOverlay.js";
import type { AutoTestResult } from "./DebugOverlay.js";
import { computeFirstPersonEye } from "./cameraUtils.js";
import { detectNewFire } from "./fireDetection.js";

const LEAN_SCALE = 0.055;       // radians of body roll per m/s of lateral speed at full speed
const LEAN_DRIFT_MULT = 1.0;    // extra roll multiplier when handbrake is held
const LEAN_SMOOTH = 0.12;       // lean low-pass time constant (seconds)
const LEAN_SPEED_REF = 40;      // m/s at which full LEAN_SCALE applies; scales linearly below
const DEATH_SPIN_INITIAL = 8.0; // rad/s initial spin rate on death
const DEATH_SPIN_DECAY = 2.5;   // exponential decay constant (1/s)
const IMPACT_SPIN_SCALE = 0.18; // rad/s of spin per m/s of approach speed on collision
const IMPACT_SPIN_MIN = 5;      // m/s approach speed below which no spin is added

/**
 * Everything needed to start a match. Replaces the old 15-positional-argument
 * `Game.start(...)` chain. Optional fields keep the exact defaults the old
 * positional signature had.
 */
export interface MatchStartOptions {
  serverUrl: string;
  driverName: string;
  blueprint: VehicleBlueprint;
  /** Carried through from the lobby; not currently read by Game. */
  keyboardOnly?: boolean;
  controlMethod?: ControlMethod;
  upgrades?: CarUpgrade;
  weaponUpgrades?: WeaponUpgrade;
  dummyOpponent?: boolean;
  dummyPreset?: DummyPreset;
  waypointThrottle?: number;
  dummyDifficulty?: string;
  gameMode?: GameMode;
  preferredTeam?: number;
  droneMode?: boolean;
  authToken?: string | null;
  /**
   * Deferred-spawn seam. When supplied, `start()` adopts this ALREADY-CONNECTED
   * network and skips both `connect()` and `enterMatch()` — the caller (the
   * quickmatch queue flow) has already joined the room and been seated by the
   * server. When omitted, `start()` behaves exactly as the drop-in path always
   * has: connect, then hello, back to back.
   */
  network?: ClientNetwork;
}

interface RemoteVisual {
  visual: VehicleVisual;
  labelSprite: THREE.Sprite;
  redrawLabel: (health: number, maxHealth: number) => void;
  flashLabel:  () => void;
  tickLabel:   (dt: number) => void;
  lastHealth: number;
  lastMaxHealth: number;
  // Cached damage zone values — updateDamageZones only called when these change.
  lastDamageFront: number;
  lastDamageBody:  number;
  lastDamageRear:  number;
  labelY: number;
  targetX: number;
  targetZ: number;
  targetRotY: number;
  bpId: string;
  alive: boolean;
  prevX: number;
  prevZ: number;
}

export class Game {
  private scene: SceneManager;
  private input: InputManager;
  private network = new ClientNetwork();
  private hud: Hud;
  private audio = new AudioSystem();
  private engineAudio!: EngineAudio;
  private physics = new PhysicsWorld();
  private arena!: ArenaRenderer;
  private effects = new EffectsSystem();
  private blueprint!: VehicleBlueprint;
  private controller!: VehicleController;
  private localVisual!: VehicleVisual;
  private localLabel!: THREE.Sprite;
  private localRedrawLabel!: (health: number, maxHealth: number) => void;
  private localFlashLabel!:  () => void;
  private localTickLabel!:   (dt: number) => void;
  private localLastHealth = -1;
  private localLastMaxHealth = -1;
  private localLastDamageFront = -1;
  private localLastDamageBody  = -1;
  private localLastDamageRear  = -1;
  private localName = "";
  private remotes = new Map<string, RemoteVisual>();
  private blueprintsById = new Map<string, VehicleBlueprint>();
  private lastNetSend = 0;
  private lastFrame = performance.now();
  private animFrame = 0;
  private wantedFire = false;
  private wantedFire2 = false;
  private wantedDrop = false;
  private localEliminated = false;
  private localLastTeamId = -1;
  private mineMeshes = new Map<string, THREE.Group>();
  private powerUpMeshes = new Map<string, THREE.Group>();

  // Reusable Sets for sync loops — clear() + add() avoids per-frame heap allocation.
  private readonly _seenRemotes     = new Set<string>();
  private readonly _seenProjectiles = new Set<string>();
  private readonly _seenMines       = new Set<string>();
  // Pre-allocated removal buffers — avoids spread-to-array in hot sync loops.
  private readonly _projToRemove: string[] = [];

  // Spectator state
  private spectateTarget: string | null = null;
  private spectateIdx = 0;

  // Camera mode — toggled by C key or touch button
  private cameraMode: CameraMode = "thirdPerson";

  // Drone observer mode
  private clientMode: "drive" | "drone" = "drive";
  private drone: DroneCameraController | null = null;

  // Body lean + death spin state
  private leanAngle = 0;
  private deathSpinRate = 0; // rad/s, signed: positive = CCW, negative = CW

  // Render mode — toggled by N key; rebuilt on each change
  private renderMode: RenderMode = "solid";
  private renderModeKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  // Audio state tracking
  private prevLocalAlive = true;
  private prevBoostActive = false;
  private prevOwnLastFiredAt = 0;

  // Client-side fire prediction
  private localReloadTimer = 0;   // ms remaining before next predicted fire
  private predictedFire = false;  // true while waiting for server to confirm
  private predictedFireAt = 0;    // performance.now() when we predicted

  // FPS tracking (rolling buffer of frame deltas, seconds)
  private frameDeltas: number[] = [];

  // Debug overlay (DEV only — null in production builds)
  private debugOverlay: DebugOverlay | null = null;
  private presetTopSpeed = 0;
  private weaponReloadMs = 600;
  private weaponUpgrades: WeaponUpgrade = DEFAULT_WEAPON_UPGRADES;

  // Auto-test (activated by ?autotest URL param)
  private isAutoTest = false;
  private autoTestElapsed = 0;
  private autoTestDuration = 10;
  private autoTestStarted = false;
  private autoTestDone = false;
  private autoTestSamples: number[] = [];

  private _lastSeenServerTime = 0;
  private _lastPatchAt = 0;

  constructor(
    canvas: HTMLCanvasElement,
    private startBlueprintLookup: (id: string) => VehicleBlueprint,
    gamepadIndex = 0,
  ) {
    this.scene = new SceneManager(canvas);
    this.input = new InputManager(gamepadIndex);
    this.hud = new Hud();
    this.input.onModeChange((m) => this.hud.setInputMode(m));
    this.input.onGamepadConnectionChange((c, n) => this.hud.showGamepadConnection(c, n));
    this.input.onSpectateNext(() => {
      if (this.clientMode === "drone" && this.drone) {
        this.drone.cycleTarget(this.getAlivePlayerIds(this.network.snapshot()));
        return;
      }
      if (!this.localEliminated) return;
      this.cycleSpectate(this.network.snapshot());
    });
    this.input.onCameraToggle(() => this.toggleCameraMode());
    this.input.onMirrorCycle(() => this.scene.cycleMirrorMode());
  }

  registerBlueprint(bp: VehicleBlueprint): void {
    this.blueprintsById.set(bp.id, bp);
  }

  getNetwork(): ClientNetwork {
    return this.network;
  }

  setRenderMode(mode: RenderMode): void {
    if (this.renderMode === mode) return;
    this.renderMode = mode;
    if (this.localVisual) this.rebuildAllVehicleVisuals();
  }

  getRenderMode(): RenderMode {
    return this.renderMode;
  }

  private rebuildAllVehicleVisuals(): void {
    // Rebuild local visual
    const oldLocal = this.localVisual;
    const newLocal = buildVehicleVisual(this.blueprint, this.renderMode, this.weaponUpgrades?.streams ?? 0);
    newLocal.group.position.copy(oldLocal.group.position);
    newLocal.group.rotation.copy(oldLocal.group.rotation);
    newLocal.group.scale.copy(oldLocal.group.scale);
    newLocal.group.visible = oldLocal.group.visible;
    this.scene.scene.remove(oldLocal.group);
    this.scene.scene.add(newLocal.group);
    this.localVisual = newLocal;
    // Force damage zone re-apply next frame
    this.localLastDamageFront = -1;
    this.localLastDamageBody  = -1;
    this.localLastDamageRear  = -1;

    // Rebuild all remote visuals
    this.remotes.forEach((v) => {
      const bp = this.blueprintsById.get(v.bpId);
      if (!bp) return;
      const oldGroup = v.visual.group;
      const newVisual = buildVehicleVisual(bp, this.renderMode, 0);
      newVisual.group.position.copy(oldGroup.position);
      newVisual.group.rotation.copy(oldGroup.rotation);
      newVisual.group.scale.copy(oldGroup.scale);
      newVisual.group.visible = oldGroup.visible;
      this.scene.scene.remove(oldGroup);
      this.scene.scene.add(newVisual.group);
      v.visual = newVisual;
      v.lastDamageFront = -1;
      v.lastDamageBody  = -1;
      v.lastDamageRear  = -1;
    });
  }

  async start(options: MatchStartOptions): Promise<void> {
    const {
      serverUrl,
      driverName: name,
      blueprint,
      controlMethod = "keyboard",
      upgrades = DEFAULT_UPGRADES,
      weaponUpgrades = DEFAULT_WEAPON_UPGRADES,
      dummyOpponent = false,
      dummyPreset = "circle",
      waypointThrottle,
      dummyDifficulty,
      gameMode = "lms",
      preferredTeam = 0,
      droneMode = false,
      authToken = null,
    } = options;
    if (droneMode) {
      this.clientMode = "drone";
      this.drone = new DroneCameraController();
    }
    this.input.setControlMethod(controlMethod);
    this.blueprint = blueprint;
    this.localName = name;
    this.weaponUpgrades = weaponUpgrades;
    this.registerBlueprint(blueprint);

    // Compute stats once — single source of truth for audio init, debug overlay, and controller.
    const presetStats = computeStats(blueprint, upgrades);
    const weaponStats = computeWeaponStats(blueprint, weaponUpgrades);
    this.weaponReloadMs = weaponStats.reloadMs;

    this.engineAudio = new EngineAudio(this.audio);
    void this.engineAudio.init(presetStats.topSpeed);

    console.log("[game] building scene…");
    this.hud.setStatus("BUILDING");
    await this.physics.init();
    this.arena = new ArenaRenderer(DEFAULT_ARENA_LAYOUT);
    this.scene.scene.add(this.arena.group);
    this.scene.scene.add(this.effects.group);

    this.localVisual = buildVehicleVisual(blueprint, this.renderMode, this.weaponUpgrades?.streams ?? 0);
    this.scene.scene.add(this.localVisual.group);

    // Local player name label
    const { sprite: localSprite, redraw: localRedraw, flash: localFlash, tickLabel: localTick } = makeNameLabel(name, blueprint.style.trimColor, true);
    this.localLabel = localSprite;
    this.localRedrawLabel = localRedraw;
    this.localFlashLabel  = localFlash;
    this.localTickLabel   = localTick;
    this.scene.scene.add(localSprite);

    const spawnPoints = DEFAULT_ARENA_LAYOUT.spawnPoints;
    const sp = spawnPoints[Math.floor(Math.random() * spawnPoints.length)];
    this.controller = new VehicleController(DEFAULT_ARENA_LAYOUT.obstacles, blueprint, {
      x: sp.x,
      y: 0,
      z: sp.z,
      rotY: sp.rot,
    }, { arenaSize: DEFAULT_ARENA_LAYOUT.size });

    this.controller.applyUpgrades(upgrades);
    this.controller.setActivePreset(findPresetName(upgrades));
    this.presetTopSpeed = presetStats.topSpeed;

    if (import.meta.env.DEV) {
      console.log(`[build] ${findPresetName(upgrades)} — topSpeed: ${presetStats.topSpeed.toFixed(1)}, accel: ${presetStats.accel.toFixed(1)}`);
      this.debugOverlay = new DebugOverlay();
      this.isAutoTest = new URLSearchParams(window.location.search).has("autotest");
      if (this.isAutoTest) console.log("[autotest] mode active — will drive at full throttle for 10 s");
    }

    this.hud.setVehicleName(blueprint.id, blueprint.name);
    this.hud.setLocalReloadMs(weaponStats.reloadMs);
    this.hud.setVehicleBlueprint(blueprint);
    this.hud.setCameraMode(this.cameraMode);
    this.hud.bindAudio(this.audio);

    if (options.network) {
      // QUEUE path: the caller already connected and already sent `hello`; the
      // server has seated us and started the round. Adopt the live room as-is —
      // re-sending hello here would re-equip us mid-round.
      this.network = options.network;
    } else {
      console.log("[game] connecting to", serverUrl, "…");
      this.hud.setStatus("CONNECTING");
      await this.network.connect({
        serverUrl,
        driverName: name,
        dummyOpponent,
        dummyPreset,
        waypointThrottle,
        dummyDifficulty,
        gameMode,
        authToken,
      });
      // DROP-IN path: connect and spawn back to back, exactly as before.
      this.network.enterMatch({
        driverName: name,
        blueprint,
        weaponUpgrades,
        preferredTeam,
      });
    }

    console.log("[game] connected — starting loop");
    this.hud.setStatus("CONNECTED");
    this.hud.setInputMode(this.input.getMode());

    // N key cycles render modes
    const MODES: RenderMode[] = ["solid", "wireframe", "toon"];
    this.renderModeKeyHandler = (e: KeyboardEvent) => {
      if (e.key === "n" || e.key === "N") {
        const next = MODES[(MODES.indexOf(this.renderMode) + 1) % MODES.length];
        this.setRenderMode(next);
      }
    };
    window.addEventListener("keydown", this.renderModeKeyHandler);

    this.lastFrame = performance.now();
    this.loop();
  }

  private loop = (): void => {
    const now = performance.now();
    let dt = (now - this.lastFrame) / 1000;
    if (dt > 0.1) dt = 0.1;
    this.lastFrame = now;

    // Rolling FPS (last 60 frames)
    this.frameDeltas.push(dt);
    if (this.frameDeltas.length > 60) this.frameDeltas.shift();
    const fps = this.frameDeltas.length / this.frameDeltas.reduce((a, b) => a + b, 1e-10);

    const networkSnap = this.network.snapshot();

    if (networkSnap.serverTime !== this._lastSeenServerTime) {
      this._lastSeenServerTime = networkSnap.serverTime;
      this._lastPatchAt = performance.now();
    }

    // Camera input is read every frame and drives SceneManager orbit state
    const camInput: CameraInput = this.input.readCamera();
    this.scene.updateFreeLook(camInput.freeLookX, camInput.freeLookY, dt);

    const input = (this.localEliminated || this.clientMode === "drone")
      ? { throttle: 0, brake: 0, steer: 0, handbrake: false, fire: false, boost: false, dropWeapon: false, yaw: 0 }
      : this.input.read();
    if (input.dropWeapon) this.wantedDrop = true;

    // Decrement local reload gate and expire stale predictions
    this.localReloadTimer = Math.max(0, this.localReloadTimer - dt * 1000);
    if (this.predictedFire && now - this.predictedFireAt > this.weaponReloadMs * 1.5) {
      // Server didn't confirm in time — clear prediction so next input is not gated
      this.predictedFire = false;
      this.localReloadTimer = 0;
    }

    // Auto-test: drive at full throttle for 10 s, record speed, then report
    let physicsInput = input;
    if (this.isAutoTest && !this.localEliminated && networkSnap.ownId) {
      if (!this.autoTestStarted) { this.autoTestStarted = true; this.autoTestElapsed = 0; }
      if (!this.autoTestDone) {
        physicsInput = { throttle: 1, brake: 0, steer: 0, handbrake: false, fire: false, boost: false, dropWeapon: false, yaw: 0 };
        this.autoTestElapsed += dt;
        this.debugOverlay?.showAutoTestRunning(this.autoTestElapsed, this.autoTestDuration);
      }
    }

    // Overdrive power-up: movement is client-authoritative, so the boost is
    // applied HERE when the server-visible effect is active. Compared against
    // serverTime (same clock the server stamped overdriveUntil with), so
    // client clock skew cannot extend or shorten the effect.
    const meState = networkSnap.players.get(networkSnap.ownId);
    const overdriveActive =
      !!meState && networkSnap.serverTime > 0 && (meState.overdriveUntil ?? 0) > networkSnap.serverTime;
    this.controller.setEffectMultipliers(
      overdriveActive ? OVERDRIVE_SPEED_MULTIPLIER : 1,
      overdriveActive ? OVERDRIVE_ACCEL_MULTIPLIER : 1,
    );

    const selfCollisions = this.controller.applyInput(physicsInput, dt);
    if (!this.localEliminated) {
      const colliders = this.buildCarColliders();
      const hitEvents = this.controller.applyCarCollisions(colliders);
      const sinR = Math.sin(this.controller.rotY);
      const cosR = Math.cos(this.controller.rotY);
      for (const ev of hitEvents) {
        if (ev.id) {
          this.network.sendCollision({ targetId: ev.id, approachSpeed: ev.approachSpeed, nx: ev.nx, nz: ev.nz });
        }
        // Impact spin — signed by which side was hit (lateral component of impact force)
        if (ev.approachSpeed > IMPACT_SPIN_MIN) {
          const mag = (ev.approachSpeed - IMPACT_SPIN_MIN) * IMPACT_SPIN_SCALE;
          const lateral = ev.nx * cosR - ev.nz * sinR;
          const signed = (lateral >= 0 ? 1 : -1) * mag;
          if (Math.abs(signed) > Math.abs(this.deathSpinRate)) this.deathSpinRate = signed;
        }
      }
      for (const ev of selfCollisions) {
        this.network.sendSelfCollision({ approachSpeed: ev.approachSpeed, impactSeverity: ev.impactSeverity, nx: ev.nx, nz: ev.nz, tag: ev.tag });
        if (ev.approachSpeed > IMPACT_SPIN_MIN) {
          const mag = (ev.approachSpeed - IMPACT_SPIN_MIN) * IMPACT_SPIN_SCALE;
          const lateral = ev.nx * cosR - ev.nz * sinR;
          const signed = (lateral >= 0 ? 1 : -1) * mag;
          if (Math.abs(signed) > Math.abs(this.deathSpinRate)) this.deathSpinRate = signed;
        }
      }
    }

    // Apply spin delta to the real physics heading BEFORE snapshot so that
    // snap.rotY, sendState, and the render all use the same spun heading.
    if (Math.abs(this.deathSpinRate) > 0.01) {
      this.controller.rotY += this.deathSpinRate * dt;
      this.deathSpinRate *= Math.exp(-DEATH_SPIN_DECAY * dt);
    } else {
      this.deathSpinRate = 0;
    }

    const snap = this.controller.snapshot();

    // Record autotest sample after physics step
    if (this.isAutoTest && this.autoTestStarted && !this.autoTestDone) {
      this.autoTestSamples.push(Math.abs(snap.speed));
      if (this.autoTestElapsed >= this.autoTestDuration) {
        this.autoTestDone = true;
        const fps = this.autoTestSamples.length / this.autoTestDuration;
        const tail = this.autoTestSamples.slice(-Math.round(fps * 2));
        const result: AutoTestResult = {
          maxSpeed:    Math.max(...this.autoTestSamples),
          avgFinal2s:  tail.reduce((a, b) => a + b, 0) / (tail.length || 1),
          topSpeedStat: this.presetTopSpeed,
        };
        this.debugOverlay?.showAutoTestResult(result);
        console.table({
          "maxSpeed reached": result.maxSpeed.toFixed(2),
          "avg final 2s":     result.avgFinal2s.toFixed(2),
          "topSpeed stat":    result.topSpeedStat.toFixed(1),
          "% of stat reached": ((result.maxSpeed / result.topSpeedStat) * 100).toFixed(1) + "%",
        });
      }
    }

    if (!this.localEliminated) this.engineAudio.update(physicsInput, snap, dt, this.controller.getGearShiftSpeed());

    // Body lean — rolls into lateral movement, stronger at higher speeds
    const speedFactor = Math.min(1, Math.abs(snap.speed) / LEAN_SPEED_REF);
    const leanFactor = physicsInput.handbrake ? LEAN_DRIFT_MULT : 1.0;
    const targetLean = -snap.latSpeed * LEAN_SCALE * speedFactor * leanFactor;
    this.leanAngle += (targetLean - this.leanAngle) * (1 - Math.exp(-dt / LEAN_SMOOTH));

    this.localVisual.group.position.set(snap.x, 0, snap.z);
    this.localVisual.group.rotation.y = snap.rotY; // spin is baked into controller.rotY
    this.localVisual.group.rotation.z = this.leanAngle;
    this.localLabel.position.set(snap.x, this.blueprint.body.height + 1.8, snap.z);

    // Camera: drone observer takes priority, then spectator, then drive modes
    if (this.clientMode === "drone" && this.drone) {
      const droneInput = this.input.readDrone(dt);
      this.drone.update(
        droneInput,
        (id) => this.remotes.get(id)?.visual.group ?? null,
        this.getAlivePlayerIds(networkSnap),
        dt,
      );
      this.scene.updateDroneCamera(this.drone.position, this.drone.yaw, this.drone.pitch);
      const ftId = this.drone.followTargetId;
      const ftName = ftId ? (networkSnap.players.get(ftId)?.name ?? "") : "";
      this.hud.setCameraLabel(ftId ? `CAM: DRONE FOLLOW ${ftName}` : "CAM: DRONE FREE");
    } else if (this.localEliminated && this.spectateTarget) {
      const remote = this.remotes.get(this.spectateTarget);
      if (remote) {
        const rpos = remote.visual.group.position;
        this.scene.followTarget(
          new THREE.Vector3(rpos.x, 1, rpos.z),
          remote.visual.group.rotation.y,
          new THREE.Vector3(0, 0, 0),
          dt,
        );
        this.scene.updateMirrorCamera(rpos.x, 1, rpos.z, remote.visual.group.rotation.y);
        const targetPlayer = networkSnap.players.get(this.spectateTarget);
        this.hud.setSpectateHint(targetPlayer?.name ?? "");
      } else {
        this.cycleSpectate(networkSnap);
      }
    } else if (this.cameraMode === "firstPerson" && !this.localEliminated) {
      const { eyeAboveGround, eyeForward } = computeFirstPersonEye(
        this.blueprint, DEFAULT_GAME_CONFIG.camera,
      );
      this.scene.followTargetFirstPerson(
        new THREE.Vector3(snap.x, 0, snap.z),
        snap.rotY,
        eyeAboveGround,
        eyeForward,
        dt,
      );
      this.scene.updateMirrorCamera(snap.x, 1, snap.z, snap.rotY);
    } else {
      this.scene.followTarget(
        new THREE.Vector3(snap.x, 1, snap.z),
        snap.rotY,
        new THREE.Vector3(snap.vx, 0, snap.vz),
        dt,
        camInput.lookBehind,
      );
      this.scene.updateMirrorCamera(snap.x, 1, snap.z, snap.rotY);
    }

    this.arena.update(now / 1000);
    this.effects.update(dt);
    this.syncRemotes(dt, networkSnap);

    // Tick flash on local visual and label every frame
    tickFlash(this.localVisual, dt);
    const brakeLocked = physicsInput.brake > 0.7 && Math.abs(snap.speed) > 5 && !physicsInput.handbrake;
    tickWheelSpin(this.localVisual, snap.speed, brakeLocked, dt);
    this.localTickLabel(dt);

    // Client-side fire prediction: fire audio + muzzle flash immediately on valid input
    if (input.fire && !this.localEliminated && this.clientMode !== "drone" && this.localReloadTimer <= 0) {
      this.localReloadTimer = this.weaponReloadMs;
      this.predictedFire = true;
      this.predictedFireAt = now;
      const fwdX = Math.sin(snap.rotY);
      const fwdZ = Math.cos(snap.rotY);
      const muzzleOff = this.blueprint.body.length * 0.5 + 0.4;
      const streamsLvl = this.weaponUpgrades?.streams ?? 0;
      const muzzleBaseX = snap.x + fwdX * muzzleOff;
      const muzzleBaseZ = snap.z + fwdZ * muzzleOff;
      const flashType = this.currentCarryWeaponType(networkSnap);
      if (streamsLvl === 1) {
        const perpX =  fwdZ * 0.25;
        const perpZ = -fwdX * 0.25;
        this.effects.spawnMuzzleFlash(muzzleBaseX - perpX, this.blueprint.body.height * 0.6, muzzleBaseZ - perpZ, fwdX, fwdZ, flashType);
        this.effects.spawnMuzzleFlash(muzzleBaseX + perpX, this.blueprint.body.height * 0.6, muzzleBaseZ + perpZ, fwdX, fwdZ, flashType);
      } else {
        this.effects.spawnMuzzleFlash(muzzleBaseX, this.blueprint.body.height * 0.6, muzzleBaseZ, fwdX, fwdZ, flashType);
      }
      this.scene.bumpShake(0.05);
      this.audio.playFire(this.weaponReloadMs);
      this.wantedFire = true;
    }

    if (input.fire2 && !this.localEliminated && this.clientMode !== "drone") {
      this.wantedFire2 = true;
    }

    // Send state to server at 20 Hz
    if (!this.localEliminated && now - this.lastNetSend > 50) {
      this.lastNetSend = now;
      this.network.sendState({
        x: snap.x,
        y: 1,
        z: snap.z,
        rotY: snap.rotY,
        vx: snap.vx,
        vy: 0,
        vz: snap.vz,
        fire: this.wantedFire,
        fire2: this.wantedFire2,
        boost: input.boost,
        dropWeapon: this.wantedDrop,
      });
      // Boost activation sound
      const boostNowActive = input.boost && (networkSnap.players.get(networkSnap.ownId)?.boostEnergy ?? 0) > 0;
      if (boostNowActive && !this.prevBoostActive) this.audio.playBoost();
      this.prevBoostActive = boostNowActive;
      this.wantedFire = false;
      this.wantedFire2 = false;
      this.wantedDrop = false;
    }

    this.hud.update(dt, networkSnap);

    // Announcer: own kills/streaks/first blood/death/lead, self pickups,
    // incoming homing rockets and round-clock thresholds — all diffed off the
    // snapshot by the pure tracker. announce() swallows every audio failure.
    for (const line of matchAnnouncer.update(networkSnap)) announcer.announce(line);

    if (!this.autoTestDone) {
      const maxSpeed = this.presetTopSpeed;
      const lagMs = networkSnap.serverTime > 0 ? Math.round(Date.now() - networkSnap.serverTime) : 0;
      this.debugOverlay?.update({
        vehicleId:    this.blueprint.id,
        vehicleName:  this.blueprint.name,
        preset:       this.controller.getActivePreset(),
        topSpeed:     maxSpeed,
        currentSpeed: snap.speed,
        speedRatio:   maxSpeed > 0 ? Math.abs(snap.speed) / maxSpeed : 0,
        boostActive:  physicsInput.boost,
        fps:          Math.round(fps),
        frameMs:      dt * 1000,
        players:      networkSnap.players.size,
        projectiles:  networkSnap.projectiles.size,
        mines:        networkSnap.mines.size,
        fxBursts:     this.effects.activeBursts,
        lagMs,
        reloadMs:     Math.round(this.localReloadTimer),
      });
    }

    const fresh = this.network.consumeFreshFx(networkSnap);
    for (const fx of fresh) {
      const dxFx = fx.x - snap.x;
      const dzFx = fx.z - snap.z;
      const distSq = dxFx * dxFx + dzFx * dzFx;
      if (fx.kind.startsWith("hit-")) {
        this.effects.spawnHit(fx.x, fx.y, fx.z);
        this.scene.bumpShake(0.1);
        this.flashClosestVehicle(networkSnap, fx.x, fx.z);
        this.audio.playHit(distSq);
      } else if (fx.kind.startsWith("explode-")) {
        this.effects.spawnExplosion(fx.x, fx.y, fx.z);
        this.scene.bumpShake(0.45);
        this.flashClosestVehicle(networkSnap, fx.x, fx.z);
        this.audio.playExplosion(distSq);
      } else if (fx.kind.startsWith("fizzle")) {
        const colonIdx = fx.kind.indexOf(":");
        const fizzleWeapon = colonIdx >= 0 ? fx.kind.slice(colonIdx + 1, fx.kind.lastIndexOf("-")) : "";
        this.effects.spawnFizzle(fx.x, fx.y, fx.z, fizzleWeapon);
      } else if (fx.kind.startsWith("wall-hit-")) {
        this.effects.spawnHit(fx.x, fx.y, fx.z, 0xa855f7);
      } else if (fx.kind.startsWith("powerup-pickup-")) {
        // Collect flash — golden burst at the pickup point. (Audio hook can
        // key off this same fx kind later.)
        this.effects.spawnHit(fx.x, fx.y, fx.z, 0xfacc15);
      } else if (fx.kind.startsWith("mine-explode-")) {
        this.effects.spawnExplosion(fx.x, fx.y, fx.z);
        this.scene.bumpShake(0.3);
        this.flashClosestVehicle(networkSnap, fx.x, fx.z);
        this.audio.playExplosion(distSq);
      } else if (fx.kind.startsWith("rocket-explode-")) {
        // Rocket detonation — bigger than a plasma hit, smaller than a kill.
        this.effects.spawnExplosion(fx.x, fx.y, fx.z, 0.7);
        this.scene.bumpShake(0.3);
        this.flashClosestVehicle(networkSnap, fx.x, fx.z);
        this.audio.playExplosion(distSq);
      } else if (fx.kind.startsWith("rocket-launch-")) {
        this.effects.spawnRocketLaunch(fx.x, fx.y, fx.z);
        this.audio.playFire(1800);
      } else if (fx.kind.startsWith("shock-")) {
        this.effects.spawnShockwave(fx.x, fx.y, fx.z);
        this.scene.bumpShake(0.35);
        this.audio.playExplosion(distSq);
      } else if (fx.kind.startsWith("bounce-")) {
        this.effects.spawnBounce(fx.x, fx.y, fx.z);
      }
    }

    // Own-fire: server confirmation syncs local reload timer.
    // Audio + flash were already played by prediction; only play here if we didn't predict.
    const ownPlayer = networkSnap.players.get(networkSnap.ownId);
    if (detectNewFire(ownPlayer, this.prevOwnLastFiredAt)) {
      this.prevOwnLastFiredAt = ownPlayer!.lastFiredAt;
      if (!this.predictedFire) {
        // Server fired but prediction didn't run (e.g. first shot after connect, or reload mismatch)
        const fwdX = Math.sin(snap.rotY);
        const fwdZ = Math.cos(snap.rotY);
        const muzzleOff = this.blueprint.body.length * 0.5 + 0.4;
        const streamsLvl = this.weaponUpgrades?.streams ?? 0;
        const muzzleBaseX = snap.x + fwdX * muzzleOff;
        const muzzleBaseZ = snap.z + fwdZ * muzzleOff;
        const flashType = this.currentCarryWeaponType(networkSnap);
        if (streamsLvl === 1) {
          const perpX =  fwdZ * 0.25;
          const perpZ = -fwdX * 0.25;
          this.effects.spawnMuzzleFlash(muzzleBaseX - perpX, this.blueprint.body.height * 0.6, muzzleBaseZ - perpZ, fwdX, fwdZ, flashType);
          this.effects.spawnMuzzleFlash(muzzleBaseX + perpX, this.blueprint.body.height * 0.6, muzzleBaseZ + perpZ, fwdX, fwdZ, flashType);
        } else {
          this.effects.spawnMuzzleFlash(muzzleBaseX, this.blueprint.body.height * 0.6, muzzleBaseZ, fwdX, fwdZ, flashType);
        }
        this.scene.bumpShake(0.05);
        this.audio.playFire(this.weaponReloadMs);
      }
      this.predictedFire = false;
      this.localReloadTimer = this.weaponReloadMs;
    }

    this.syncProjectiles(networkSnap);
    this.syncMines(networkSnap);
    this.syncPowerUps(networkSnap);
    this.applyServerForLocal(networkSnap);
    // Visibility depends on both elimination state and camera mode — recompute each frame
    this.updateLocalVisibility();

    this.scene.render();
    this.animFrame = requestAnimationFrame(this.loop);
  };

  /**
   * Which weapon the PRIMARY trigger will actually fire right now — mirrors
   * the server's carry priority (homing > ricochet > shockwave > blueprint
   * primary). Drives the muzzle-flash colour/shape for prediction.
   */
  private currentCarryWeaponType(snap: ReturnType<ClientNetwork["snapshot"]>): string {
    const me = snap.players.get(snap.ownId);
    if (me) {
      if (me.homingMissiles > 0) return "homing-rocket";
      if ((me.ricochetShots ?? 0) > 0) return "ricochet";
      if ((me.shockwaveCharges ?? 0) > 0) return "shockwave";
    }
    return this.blueprint.weapon.type;
  }

  private buildCarColliders(): Array<{ id: string; x: number; z: number; hx: number; hz: number }> {
    const result: Array<{ id: string; x: number; z: number; hx: number; hz: number }> = [];
    this.remotes.forEach((v, id) => {
      if (!v.alive) return;
      const bp = this.blueprintsById.get(v.bpId);
      if (!bp) return;
      const pos = v.visual.group.position;
      result.push({ id, x: pos.x, z: pos.z, hx: bp.body.width / 2, hz: bp.body.length / 2 });
    });
    return result;
  }

  private flashClosestVehicle(snap: ReturnType<ClientNetwork["snapshot"]>, fx: number, fz: number): void {
    let closestId: string | null = null;
    let closestDist = 4;
    snap.players.forEach((p, id) => {
      const dx = p.x - fx;
      const dz = p.z - fz;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < closestDist) {
        closestDist = d;
        closestId = id;
      }
    });
    if (!closestId) return;
    if (closestId === snap.ownId) {
      triggerFlash(this.localVisual);
      this.localFlashLabel();
    } else {
      const remote = this.remotes.get(closestId);
      if (remote) {
        triggerFlash(remote.visual);
        remote.flashLabel();
      }
    }
  }

  private syncRemotes(dt: number, snap: ReturnType<ClientNetwork["snapshot"]>): void {
    const seen = this._seenRemotes;
    seen.clear();

    // Update spectate target list
    const aliveIds: string[] = [];
    snap.players.forEach((p, id) => {
      if (id !== snap.ownId && !p.eliminated) aliveIds.push(id);
    });
    // Validate spectate target still exists
    if (this.spectateTarget && !aliveIds.includes(this.spectateTarget)) {
      this.cycleSpectate(snap);
    }

    snap.players.forEach((p) => {
      if (p.id === snap.ownId) return;
      seen.add(p.id);

      let v = this.remotes.get(p.id);
      const bp =
        this.blueprintsById.get(p.blueprintId) ??
        this.startBlueprintLookup(p.blueprintId);

      if (!v || v.bpId !== p.blueprintId) {
        if (v) {
          this.scene.scene.remove(v.visual.group);
          this.scene.scene.remove(v.labelSprite);
        }
        const newVisual = buildVehicleVisual(bp, this.renderMode, 0);
        this.scene.scene.add(newVisual.group);
        const remColor = p.teamId > 0 ? teamColor(p.teamId) : bp.style.trimColor;
        const { sprite: remSprite, redraw: remRedraw, flash: remFlash, tickLabel: remTick } = makeNameLabel(p.name, remColor, false);
        this.scene.scene.add(remSprite);
        v = {
          visual: newVisual,
          labelSprite: remSprite,
          redrawLabel: remRedraw,
          flashLabel:  remFlash,
          tickLabel:   remTick,
          lastHealth: -1,
          lastMaxHealth: -1,
          lastDamageFront: -1,
          lastDamageBody:  -1,
          lastDamageRear:  -1,
          labelY: bp.body.height + 1.8,
          targetX: p.x,
          targetZ: p.z,
          targetRotY: p.rotY,
          bpId: p.blueprintId,
          alive: p.alive,
          prevX: p.x,
          prevZ: p.z,
        };
        this.remotes.set(p.id, v);
      }

      v.targetX = p.x;
      v.targetZ = p.z;
      v.targetRotY = p.rotY;
      v.alive = p.alive;
      v.visual.group.visible = p.alive || p.eliminated === false;
      v.labelSprite.visible = p.alive;
      if (p.health !== v.lastHealth || p.maxHealth !== v.lastMaxHealth) {
        v.redrawLabel(p.health, p.maxHealth);
        v.lastHealth = p.health;
        v.lastMaxHealth = p.maxHealth;
      }
      if (p.damageFront !== v.lastDamageFront || p.damageBody !== v.lastDamageBody || p.damageRear !== v.lastDamageRear) {
        updateDamageZones(v.visual, p.damageFront, p.damageBody, p.damageRear, p.maxHealth);
        v.lastDamageFront = p.damageFront;
        v.lastDamageBody  = p.damageBody;
        v.lastDamageRear  = p.damageRear;
      }
    });

    const t = 1 - Math.pow(0.001, dt);
    this.remotes.forEach((v, id) => {
      if (!seen.has(id)) {
        this.scene.scene.remove(v.visual.group);
        this.scene.scene.remove(v.labelSprite);
        this.remotes.delete(id);
        return;
      }
      const pos = v.visual.group.position;
      pos.x += (v.targetX - pos.x) * t;
      pos.y = 0;
      pos.z += (v.targetZ - pos.z) * t;
      v.visual.group.rotation.y += wrapAngleDiff(v.targetRotY - v.visual.group.rotation.y) * t;

      // Keep label above the car
      if (v.labelSprite.visible) {
        v.labelSprite.position.set(pos.x, v.labelY, pos.z);
      }

      // Tick flash + wheel spin for this remote vehicle and its label
      tickFlash(v.visual, dt);
      const remDx = pos.x - v.prevX;
      const remDz = pos.z - v.prevZ;
      const remSpeed = Math.sqrt(remDx * remDx + remDz * remDz) / dt;
      tickWheelSpin(v.visual, remSpeed, false, dt);

      // Remote body lean — estimate lateral speed from position delta vs heading
      if (dt > 0.001) {
        const rotY = v.visual.group.rotation.y;
        const remLatSpeed = (remDx * Math.cos(rotY) - remDz * Math.sin(rotY)) / dt;
        const remLeanTarget = -remLatSpeed * LEAN_SCALE;
        v.visual.group.rotation.z += (remLeanTarget - v.visual.group.rotation.z) * (1 - Math.exp(-dt / LEAN_SMOOTH));
      }

      v.prevX = pos.x;
      v.prevZ = pos.z;
      v.tickLabel(dt);
    });
  }

  private syncProjectiles(snap: ReturnType<ClientNetwork["snapshot"]>): void {
    const seen = this._seenProjectiles;
    seen.clear();
    // Extrapolate projectile positions forward by snapshot age to compensate for
    // network latency. Use monotonic clock delta since last server patch to avoid
    // wall-clock / server-clock skew.
    const ageMs = this._lastPatchAt > 0
      ? Math.min(250, performance.now() - this._lastPatchAt)
      : 0;
    const ageSec = ageMs / 1000;
    snap.projectiles.forEach((p) => {
      seen.add(p.id);
      let ex: number, ey: number, ez: number;
      if (p.isHoming) {
        ex = p.x; ey = p.y; ez = p.z;
      } else {
        ex = p.x + p.dx * p.speed * ageSec;
        ey = p.y + p.dy * p.speed * ageSec;
        ez = p.z + p.dz * p.speed * ageSec;
      }
      this.effects.upsertProjectile(p.id, ex, ey, ez, p.weaponType, p.dx, p.dz);
    });
    // Collect removes first to avoid modifying the map during iteration.
    this._projToRemove.length = 0;
    for (const id of this.effects.knownProjectiles()) {
      if (!seen.has(id)) this._projToRemove.push(id);
    }
    for (const id of this._projToRemove) this.effects.removeProjectile(id);
  }

  private syncMines(snap: ReturnType<ClientNetwork["snapshot"]>): void {
    const seen = this._seenMines;
    seen.clear();
    const now = Date.now();
    snap.mines.forEach((mine: MineView) => {
      seen.add(mine.id);
      if (!this.mineMeshes.has(mine.id)) {
        const group = buildMineMesh();
        group.position.set(mine.x, 0.08, mine.z);
        this.scene.scene.add(group);
        this.mineMeshes.set(mine.id, group);
      }
      const group = this.mineMeshes.get(mine.id)!;
      const armed = now - mine.deployedAt >= MINE_ARM_DELAY_MS;
      // Pulse the LED indicator: faster when armed
      const led = group.children[1] as THREE.Mesh;
      if (led?.material) {
        const t = (now / 1000) * (armed ? 4 : 1.5);
        const pulse = 0.55 + 0.45 * Math.sin(t * Math.PI * 2);
        (led.material as THREE.MeshBasicMaterial).color.setHex(armed ? 0xf472b6 : 0xfb923c);
        (led.material as THREE.MeshBasicMaterial).opacity = pulse;
      }
    });
    for (const [id, group] of this.mineMeshes) {
      if (!seen.has(id)) {
        this.scene.scene.remove(group);
        group.children.forEach((c) => {
          (c as THREE.Mesh).geometry?.dispose();
          ((c as THREE.Mesh).material as THREE.Material)?.dispose();
        });
        this.mineMeshes.delete(id);
      }
    }
  }

  private syncPowerUps(snap: ReturnType<ClientNetwork["snapshot"]>): void {
    const seen = new Set<string>();
    const now = Date.now();
    snap.powerUps.forEach((pu) => {
      seen.add(pu.id);
      if (!this.powerUpMeshes.has(pu.id)) {
        const group = buildPowerUpMesh(pu.type);
        group.position.set(pu.x, 0, pu.z);
        this.scene.scene.add(group);
        this.powerUpMeshes.set(pu.id, group);
      }
      const group = this.powerUpMeshes.get(pu.id)!;
      // Hide when unavailable; animate when available
      group.visible = pu.available;
      if (pu.available) {
        const t = now / 1000;
        group.rotation.y = t * 1.4;
        group.position.y = 0.6 + Math.sin(t * 2.2) * 0.18;
      }
    });
    for (const [id, group] of this.powerUpMeshes) {
      if (!seen.has(id)) {
        this.scene.scene.remove(group);
        this.powerUpMeshes.delete(id);
      }
    }
  }

  private applyServerForLocal(snap: ReturnType<ClientNetwork["snapshot"]>): void {
    if (!snap.ownId) return;
    const me: PlayerView | undefined = snap.players.get(snap.ownId);
    if (!me) return;

    // Rebuild local label when team is first assigned (color changes)
    if (me.teamId !== this.localLastTeamId) {
      this.localLastTeamId = me.teamId;
      const localColor = me.teamId > 0 ? teamColor(me.teamId) : this.blueprint.style.trimColor;
      this.scene.scene.remove(this.localLabel);
      const { sprite, redraw, flash, tickLabel } = makeNameLabel(this.localName, localColor, true);
      this.localLabel       = sprite;
      this.localRedrawLabel = redraw;
      this.localFlashLabel  = flash;
      this.localTickLabel   = tickLabel;
      this.localLastHealth  = -1; // force health redraw
      this.scene.scene.add(sprite);
    }

    if (me.health !== this.localLastHealth || me.maxHealth !== this.localLastMaxHealth) {
      this.localRedrawLabel(me.health, me.maxHealth);
      // Damage sound when health drops (but not on respawn — health going up)
      if (this.localLastHealth > 0 && me.health < this.localLastHealth && me.alive) {
        this.audio.playDamage();
      }
      this.localLastHealth = me.health;
      this.localLastMaxHealth = me.maxHealth;
    }
    if (me.damageFront !== this.localLastDamageFront || me.damageBody !== this.localLastDamageBody || me.damageRear !== this.localLastDamageRear) {
      updateDamageZones(this.localVisual, me.damageFront, me.damageBody, me.damageRear, me.maxHealth);
      this.localLastDamageFront = me.damageFront;
      this.localLastDamageBody  = me.damageBody;
      this.localLastDamageRear  = me.damageRear;
    }

    // Death / respawn audio
    if (this.prevLocalAlive && !me.alive) {
      this.audio.playDeath();
      // Clear any pending prediction so stale state doesn't carry over respawn
      this.predictedFire = false;
      this.localReloadTimer = 0;
      // Kick death spin
      this.deathSpinRate = DEATH_SPIN_INITIAL;
    } else if (!this.prevLocalAlive && me.alive) {
      this.audio.playRespawn();
      this.localReloadTimer = 0;
      this.controller.teleport(me.x, me.y, me.z, me.rotY);
      this.deathSpinRate = 0;
    }
    this.prevLocalAlive = me.alive;

    // Check for round reset: previously eliminated but now alive again
    if (!me.eliminated && this.localEliminated) {
      this.localEliminated = false;
      this.spectateTarget = null;
      this.hud.clearEliminated();
      // Visibility corrected at end of loop by updateLocalVisibility()
    }

    // Check for new elimination
    if (me.eliminated && !this.localEliminated) {
      this.localEliminated = true;
      this.hud.showEliminated();
      // Visibility corrected at end of loop by updateLocalVisibility()
      // Auto-select first alive spectate target
      const aliveIds = this.getAlivePlayerIds(snap);
      if (aliveIds.length > 0) {
        this.spectateTarget = aliveIds[0];
        this.spectateIdx = 0;
      }
      return;
    }

    const s = this.controller.snapshot();
    if (!me.alive) {
      this.controller.teleport(me.x, me.y, me.z, me.rotY);
      return;
    }
    const dx = me.x - s.x;
    const dz = me.z - s.z;
    if (dx * dx + dz * dz > 80 * 80) {
      this.controller.teleport(me.x, me.y, me.z, me.rotY);
    }
  }

  private getAlivePlayerIds(snap: ReturnType<ClientNetwork["snapshot"]>): string[] {
    const ids: string[] = [];
    snap.players.forEach((p, id) => {
      if (id !== snap.ownId && !p.eliminated) ids.push(id);
    });
    return ids;
  }

  private cycleSpectate(snap: ReturnType<ClientNetwork["snapshot"]>): void {
    const aliveIds = this.getAlivePlayerIds(snap);
    if (aliveIds.length === 0) {
      this.spectateTarget = null;
      return;
    }
    if (this.spectateTarget === null) {
      this.spectateTarget = aliveIds[0];
      this.spectateIdx = 0;
      return;
    }
    const currentIdx = aliveIds.indexOf(this.spectateTarget);
    this.spectateIdx = (currentIdx + 1) % aliveIds.length;
    this.spectateTarget = aliveIds[this.spectateIdx];
  }

  /** Toggle between third-person and first-person camera. */
  private toggleCameraMode(): void {
    if (!this.controller || !this.localVisual) return; // not yet in-game
    this.cameraMode = this.cameraMode === "thirdPerson" ? "firstPerson" : "thirdPerson";
    const snap = this.controller.snapshot();
    this.scene.setCameraMode(this.cameraMode, snap.rotY);
    this.hud.setCameraMode(this.cameraMode);
    this.input.getTouchInput().updateCamLabel(this.cameraMode);
    this.updateLocalVisibility();
  }

  /**
   * Reconciles local vehicle visibility with the current elimination state
   * and camera mode.  Called every frame so it's always correct regardless
   * of which path last changed `localEliminated` or `cameraMode`.
   */
  private updateLocalVisibility(): void {
    const show = this.clientMode !== "drone" && !this.localEliminated && this.cameraMode !== "firstPerson";
    this.localVisual.group.visible = show;
    this.localLabel.visible = show;
  }

  /**
   * Undo a partial start() so start() can be safely called again.
   * Does NOT destroy input (so the user can still interact with the lobby).
   * Called from main.ts catch handler when network.connect() rejects.
   */
  resetForRetry(): void {
    cancelAnimationFrame(this.animFrame);
    this.animFrame = 0;
    this.network.leave();
    this.engineAudio?.dispose();
    this.debugOverlay?.dispose();
    if (this.renderModeKeyHandler) {
      window.removeEventListener("keydown", this.renderModeKeyHandler);
      this.renderModeKeyHandler = null;
    }
    // Remove scene objects added during the failed start()
    if (this.arena?.group) this.scene.scene.remove(this.arena.group);
    if (this.localVisual?.group) this.scene.scene.remove(this.localVisual.group);
    if (this.localLabel) this.scene.scene.remove(this.localLabel);
    this.scene.scene.remove(this.effects.group);
    for (const [, group] of this.mineMeshes) this.scene.scene.remove(group);
    this.mineMeshes.clear();
    for (const [, group] of this.powerUpMeshes) this.scene.scene.remove(group);
    this.powerUpMeshes.clear();
    this.remotes.forEach((v) => {
      this.scene.scene.remove(v.visual.group);
      this.scene.scene.remove(v.labelSprite);
    });
    this.remotes.clear();
    // Reset flags so start() is idempotent
    this.localEliminated = false;
    this.spectateTarget = null;
    this.prevLocalAlive = true;
    this.localReloadTimer = 0;
    this.predictedFire = false;
    this.leanAngle = 0;
    this.deathSpinRate = 0;
    this.localLastHealth = -1;
    this.localLastMaxHealth = -1;
    this.localLastDamageFront = -1;
    this.localLastDamageBody  = -1;
    this.localLastDamageRear  = -1;
    this.localLastTeamId = -1;
    this.prevBoostActive = false;
    this.prevOwnLastFiredAt = 0;
    this.wantedFire = false;
    this.wantedFire2 = false;
    this.wantedDrop = false;
    this.clientMode = "drive";
    this.drone = null;
  }

  destroy(): void {
    cancelAnimationFrame(this.animFrame);
    this.network.leave();
    this.input.destroy();
    this.engineAudio?.dispose();
    this.debugOverlay?.dispose();
    if (this.renderModeKeyHandler) {
      window.removeEventListener("keydown", this.renderModeKeyHandler);
      this.renderModeKeyHandler = null;
    }
    this.drone = null;
  }
}

const LABEL_FLASH_MS = 350;

function makeNameLabel(
  name: string,
  color: string,
  isLocal: boolean,
): {
  sprite:     THREE.Sprite;
  redraw:     (health: number, maxHealth: number) => void;
  flash:      () => void;
  tickLabel:  (dt: number) => void;
} {
  const W = 160, H = 22;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(3.0, 0.41, 1);

  const labelColor = color || "#22d3ee";
  let cachedHealth = 1, cachedMax = 1;
  let flashUntil = 0;

  function draw(flashing: boolean): void {
    ctx.clearRect(0, 0, W, H);

    if (flashing) {
      // Subtle bright background wash
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      ctx.fillRect(0, 0, W, H);
    }

    // Name text
    ctx.save();
    ctx.font = "10px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (flashing) {
      ctx.shadowColor = "#ffffff";
      ctx.shadowBlur = 6;
      ctx.fillStyle = "#ffffff";
      ctx.globalAlpha = 1.0;
    } else {
      ctx.fillStyle = isLocal ? "#cccccc" : labelColor;
      ctx.globalAlpha = isLocal ? 0.75 : 0.55;
    }

    ctx.fillText(name.slice(0, 16), W / 2, H / 2 - 2);
    ctx.restore();

    // 2 px health strip along the bottom
    const ratio = cachedMax > 0 ? Math.max(0, Math.min(1, cachedHealth / cachedMax)) : 0;
    const hColor = ratio > 0.6 ? "#22c55e" : ratio > 0.3 ? "#facc15" : "#ef4444";
    ctx.globalAlpha = flashing ? 0.9 : 0.45;
    ctx.fillStyle = hColor;
    ctx.fillRect(0, H - 2, Math.floor(W * ratio), 2);
    ctx.globalAlpha = 1;

    tex.needsUpdate = true;
  }

  function redraw(health: number, maxHealth: number): void {
    cachedHealth = health;
    cachedMax    = maxHealth;
    draw(performance.now() < flashUntil);
  }

  function flash(): void {
    flashUntil = performance.now() + LABEL_FLASH_MS;
    draw(true);
  }

  function tickLabel(_dt: number): void {
    if (flashUntil > 0 && performance.now() > flashUntil) {
      flashUntil = 0;
      draw(false);
    }
  }

  draw(false);
  return { sprite, redraw, flash, tickLabel };
}

function buildMineMesh(): THREE.Group {
  const group = new THREE.Group();
  // Body: dark flat disc
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.38, 0.4, 0.14, 16),
    new THREE.MeshBasicMaterial({ color: 0x1a1a2e }),
  );
  // LED indicator on top: small glowing disc
  const led = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.1, 0.02, 12),
    new THREE.MeshBasicMaterial({ color: 0xfb923c, transparent: true, opacity: 0.9 }),
  );
  led.position.y = 0.08;
  group.add(body, led);
  return group;
}

/**
 * Per-type pickup look: distinct colour + gem silhouette per power-up so each
 * is readable at a glance in-world. Same mesh approach for all (wireframe gem
 * + translucent core + floor beam + label sprite) — no external assets.
 */
const POWERUP_STYLES: Record<string, { color: number; hex: string; label: string; gem: () => THREE.BufferGeometry; gemScaleY: number }> = {
  "homing-missiles": { color: 0xa855f7, hex: "#a855f7", label: "HOMING",     gem: () => new THREE.OctahedronGeometry(0.55, 0),     gemScaleY: 1.4 },
  "wide-mines":      { color: 0x22d3ee, hex: "#22d3ee", label: "WIDE MINES", gem: () => new THREE.OctahedronGeometry(0.55, 0),     gemScaleY: 1.4 },
  "overdrive":       { color: 0xfb923c, hex: "#fb923c", label: "OVERDRIVE",  gem: () => new THREE.ConeGeometry(0.5, 1.1, 4),       gemScaleY: 1.0 },
  "spread5":         { color: 0xfacc15, hex: "#facc15", label: "5-WAY",      gem: () => new THREE.TorusGeometry(0.45, 0.14, 6, 12), gemScaleY: 1.0 },
  "armour":          { color: 0x4ade80, hex: "#4ade80", label: "ARMOUR",     gem: () => new THREE.IcosahedronGeometry(0.55, 0),    gemScaleY: 1.15 },
  "ricochet":        { color: 0x22c55e, hex: "#22c55e", label: "RICOCHET",   gem: () => new THREE.TetrahedronGeometry(0.62, 0),    gemScaleY: 1.0 },
  "shockwave":       { color: 0x60a5fa, hex: "#60a5fa", label: "SHOCKWAVE",  gem: () => new THREE.DodecahedronGeometry(0.5, 0),    gemScaleY: 1.0 },
};
const POWERUP_STYLE_FALLBACK = POWERUP_STYLES["wide-mines"];

function buildPowerUpMesh(type: string): THREE.Group {
  const group = new THREE.Group();
  const style = POWERUP_STYLES[type] ?? POWERUP_STYLE_FALLBACK;
  const color = style.color;

  // Spinning wireframe gem — geometry varies per power-up type
  const mat = new THREE.MeshBasicMaterial({ color, wireframe: true });
  const gem = new THREE.Mesh(style.gem(), mat);
  gem.scale.y = style.gemScaleY;
  group.add(gem);

  // Solid inner core — slightly smaller, semi-transparent
  const innerMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.28 });
  const inner = new THREE.Mesh(style.gem(), innerMat);
  inner.scale.setScalar(0.6);
  inner.scale.y = 0.6 * style.gemScaleY;
  group.add(inner);

  // Vertical beam on floor
  const beamGeo = new THREE.CylinderGeometry(0.04, 0.04, 3, 6);
  const beamMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35 });
  const beam = new THREE.Mesh(beamGeo, beamMat);
  beam.position.y = -1.5;
  group.add(beam);

  // Floating label sprite — always faces camera, bobs with gem
  group.add(makePowerUpLabel(type));

  return group;
}

function makePowerUpLabel(type: string): THREE.Sprite {
  const style = POWERUP_STYLES[type] ?? POWERUP_STYLE_FALLBACK;
  const text = style.label;
  const hexColor = style.hex;

  const W = 128, H = 24;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);
  ctx.font = "bold 12px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = hexColor;
  ctx.shadowBlur = 12;
  ctx.fillStyle = hexColor;
  ctx.globalAlpha = 0.95;
  ctx.fillText(text, W / 2, H / 2);

  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(2.8, 0.52, 1);
  sprite.position.y = 1.35; // floats above gem; bobs with parent group
  return sprite;
}

function wrapAngleDiff(a: number): number {
  let v = ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  if (v > Math.PI) v -= Math.PI * 2;
  return v;
}

function findPresetName(u: CarUpgrade): string {
  for (const [k, v] of Object.entries(CAR_PRESETS)) {
    if ((Object.keys(u) as Array<keyof CarUpgrade>).every((key) => u[key] === v[key])) return k;
  }
  return "custom";
}
